import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef } from "preact/hooks";
import { Button } from "./Button.tsx";

export interface TourStep {
  /** A CSS selector for the part of the page the step is about; none for a step about the whole page. */
  target?: string;
  title: string; body: ComponentChildren;
  /** The forward button's words on this step. Default "Next", or "Done" on the last. */
  next?: string;
}

// A target can arrive after its data does, so a step looks for it for a moment.
const LOOKS = 12;
const LOOK_EVERY_MS = 250;

/**
 * A guided walkthrough of the page you're on: a coach card in the corner and a
 * ring around the part each step is about. Rule: a tour only points at real
 * parts of the page and never changes anything, and every step can be skipped.
 */
export function Tour({ title, steps, step, onStep, onClose }: {
  title: string; steps: ReadonlyArray<TourStep>; step: number;
  onStep: (step: number) => void;
  /** `finished` is true when the last step's button closed it, false for Skip or Escape. */
  onClose: (finished: boolean) => void;
}) {
  const ring = useRef<HTMLDivElement>(null);
  const forward = useRef<HTMLButtonElement>(null);
  const id = useId();
  const current = steps[step];
  const last = step === steps.length - 1;

  useEffect(() => {
    const box = ring.current;
    if (!box) return;
    box.hidden = true;
    const selector = current?.target;
    if (!selector) return;
    let target: HTMLElement | null = null;
    let looks = 0;
    let frame = 0;
    let timer = 0;
    const place = () => {
      if (!target) return;
      const r = target.getBoundingClientRect();
      box.hidden = r.width === 0 && r.height === 0;
      box.style.top = `${r.top - 6}px`; box.style.left = `${r.left - 6}px`;
      box.style.width = `${r.width + 12}px`; box.style.height = `${r.height + 12}px`;
    };
    const later = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(place); };
    const look = () => {
      const found = document.querySelector(selector);
      // A part that's hidden, such as the sidebar while it's closed on a phone, gets no ring.
      if (found instanceof HTMLElement && found.getClientRects().length > 0 && found.checkVisibility({ visibilityProperty: true })) {
        target = found;
        // Marks the part, so a folded container (the icon rail) can open while the tour points into it.
        found.classList.add("ui-tour-target");
        found.scrollIntoView({ block: "center", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
        place();
      } else if (++looks < LOOKS) timer = window.setTimeout(look, LOOK_EVERY_MS);
    };
    look();
    addEventListener("scroll", later, true);
    addEventListener("resize", later);
    return () => { target?.classList.remove("ui-tour-target"); clearTimeout(timer); cancelAnimationFrame(frame); removeEventListener("scroll", later, true); removeEventListener("resize", later); };
  }, [current?.target, step]);

  useEffect(() => { forward.current?.focus({ preventScroll: true }); }, [step]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!current) return null;
  return <>
    <div ref={ring} class="ui-tour__ring" aria-hidden="true" hidden />
    <section class="ui-tour" role="dialog" aria-labelledby={id}>
      <p class="ui-tour__eyebrow">{title} · {step + 1} of {steps.length}</p>
      <h2 id={id}>{current.title}</h2>
      <div class="ui-tour__body" aria-live="polite">{current.body}</div>
      <div class="ui-tour__actions">
        {!last && <Button size="sm" variant="ghost" onClick={() => onClose(false)}>Skip tour</Button>}
        <span class="ui-tour__spacer" />
        {step > 0 && <Button size="sm" onClick={() => onStep(step - 1)}>Back</Button>}
        <button ref={forward} type="button" class="ui-btn ui-btn--solid ui-btn--sm" onClick={() => (last ? onClose(true) : onStep(step + 1))}>{current.next ?? (last ? "Done" : "Next")}</button>
      </div>
    </section>
  </>;
}
